/**
 * ADR 023 — `deriveLotPhase`: the in-memory half of the lot lifecycle predicate
 * (BL-639). Pure IsomorphicService (ADR 010 / sharing.md §3) — runs identically
 * server-side (the bid gate evaluates `kind === 'open-for-bidding'`) and
 * client-side (over a lot snapshot + its auction). No I/O, no auth, no
 * `@auctogon/db` import.
 *
 * Mirror of the SQL `lotPhaseWhere` (phase-where.ts); the Postgres parity test is
 * the anti-drift gate. Precedence ladder + field semantics: phase-tokens.ts.
 */
import {
  type LotPhase,
  type LotPhaseAuctionInput,
  type LotPhaseInput,
  type LotTerminalInput,
  type TerminalLotPhase,
} from './phase-tokens';

/**
 * The terminal half of the lot precedence ladder (ADR 023). Reads ONLY the
 * frozen-at-close decision stamps — no auction inputs and no `now`. That makes
 * it safe to call from surfaces that hold a lot but no auction row.
 *
 * `deriveLotPhase` delegates its top five rungs here so the two never drift
 * (sharing.md §3/§4) — the SQL `lotPhaseWhere` parity test still gates the
 * classification, and this refactor preserves it 1:1.
 *
 * @param lot The terminal-stamp inputs (see {@link LotTerminalInput}).
 * @returns The terminal {@link LotPhase} (deleted/withdrawn/sold/unsold),
 *   or `null` when the lot has NOT reached a terminal disposition.
 */
export function deriveTerminalDisposition(lot: LotTerminalInput): TerminalLotPhase | null {
  if (lot.deletedAt != null) return { kind: 'deleted' };
  if (lot.withdrawnAt != null) {
    // BL-687: the withdrawal actor is no longer denormalized onto the lot row
    // (`withdrawn_by` / `withdrawn_by_staff_user_id` dropped). The actor lives
    // in the audit log (`public.actions`); this token carries only the stamp.
    return { kind: 'withdrawn', withdrawnAt: lot.withdrawnAt };
  }
  if (lot.hammeredAt != null) {
    return {
      kind: 'sold',
      hammeredAt: lot.hammeredAt,
      // BL-645: optional — snapshot omits the winner customer UUID.
      winnerId: lot.winnerCustomerId ?? null,
    };
  }
  if (lot.closeProcessedAt != null) {
    // unsold has no dedicated stamp — it's "resolved, not sold/withdrawn".
    // BL-1387 PR 2: the `passed` rung used to sit ABOVE this one, reading a
    // `passed_at` stamp no production path ever wrote. With it retired, a
    // reserve-not-met lot lands here and derives `unsold` — which is what the
    // close path (`determineDisposition`) always actually produced. The
    // `passed` DISPOSITION is unaffected and still recorded on
    // `auction_lots.disposition`.
    const reason = lot.bidsPlacedCountDenormalized === 0 ? 'no-bids' : 'reserve-not-met';
    return { kind: 'unsold', closedAt: lot.closeProcessedAt, reason };
  }
  return null;
}

/**
 * When a lot closed, without saying how (BUG-2114). The public lot snapshot
 * carries this ONE stamp in place of `hammered_at` + `close_processed_at`: the
 * pair told a sold lot from one that closed unsold with bids, and the close
 * sells any lot whose max covers its reserve, so the latter means "the reserve
 * was above every bid" — the fact a house that picked "Nothing" keeps private.
 *
 * The close stamp wins over the hammer so a lot that closed unsold and sold
 * afterwards (an offer) keeps the stamp it closed with. A lot sold without a
 * close (dispose, flip-to-sold) has only `hammered_at`, and reads as closed then.
 *
 * @param lot The two terminal-outcome stamps.
 * @returns The closed stamp, or `null` while the lot has not closed.
 * @sideEffect none — pure.
 */
export function deriveLotClosedAt(lot: Pick<LotTerminalInput, 'hammeredAt' | 'closeProcessedAt'>): string | null {
  return lot.closeProcessedAt ?? lot.hammeredAt ?? null;
}

/** The two auction stamps a lot's bidding windows read — see {@link deriveLotWindows}. */
export type LotWindowAuctionInput = Pick<LotPhaseAuctionInput, 'openedAt' | 'preBiddingAt'>;

/**
 * Whether each of a lot's two bidding windows is open. Both are two-stamp
 * (BL-1291): the auction's stamp THEN the lot's. A lot keeps its own stamp when
 * its auction is moved back a phase (the backward transitions write no lot row),
 * so reading the lot's stamp alone answers for the phase the auction has left.
 *
 * The one definition `deriveLotPhase` and the edit-lock read
 * (`deriveLotEditLockState`, BUG-1772) both use, so a lot's locks and its phase
 * cannot disagree about whether bidders can see it.
 *
 * @param lot The lot's two window stamps.
 * @param auction The lot's auction, or `null` for a lot in no auction (never open).
 * @returns `biddingOpen` (accepting bids) and `preBiddingOpen` (accepting pre-bids).
 * @sideEffect none — pure.
 */
export function deriveLotWindows(
  lot: Pick<LotPhaseInput, 'biddingOpenedAt' | 'preBiddingAt'>,
  auction: LotWindowAuctionInput | null,
): { biddingOpen: boolean; preBiddingOpen: boolean } {
  if (auction == null) return { biddingOpen: false, preBiddingOpen: false };
  return {
    biddingOpen: auction.openedAt != null && lot.biddingOpenedAt != null,
    preBiddingOpen: auction.preBiddingAt != null && lot.preBiddingAt != null,
  };
}

/**
 * Derive a timed lot's lifecycle phase from its decision-stamp columns + the
 * window-open inputs from its auction.
 *
 * @param input.lot Lot decision-stamp fields (see {@link LotPhaseInput}).
 * @param input.auction The lot's auction — `openedAt`, `preBiddingAt` and
 *   `closingType` are read (a lot's bidding window opens when the auction opens,
 *   or via pre-bidding — BL-1291 made BOTH windows two-stamp, auction then lot).
 *   `null` for a lot not yet added to any auction (BL-941 / BL-939's
 *   unassociated lots) — see the "null-auction (unassociated) case" doc on
 *   {@link LotPhaseInput}'s ladder comment.
 * @param input.now The clock to evaluate against. Compared by epoch ms.
 * @returns The {@link LotPhase} discriminated union.
 */
export function deriveLotPhase(input: {
  lot: LotPhaseInput;
  auction: LotPhaseAuctionInput | null;
  now: Date;
}): LotPhase {
  const { lot, auction, now } = input;

  // Terminal dispositions (stamped once at close, then frozen — ADR 023).
  // Delegated to the shared terminal reader so the terminal ladder lives in
  // one place (BL-670); everything below is the runtime/prep half. Reads
  // only lot-own columns, so this is unaffected by a null auction.
  const terminal = deriveTerminalDisposition(lot);
  if (terminal != null) return terminal;

  if (lot.pausedAt != null) return { kind: 'paused', pausedAt: lot.pausedAt };

  // BL-941 / BUG-1110: a lot not yet added to any auction (auction_id IS NULL)
  // has no auction to derive windowOpen from, so rungs 7–9 (awaiting-resolution
  // / open-for-bidding / pre-bidding) are unreachable. It is ALWAYS `draft`.
  //
  // BUG-1110 (signed off: Michael Lawrie, 2026-09-09) — this branch used to
  // substitute `cataloguing_complete` for the ready/draft decision, which made
  // "Ready" mean two different things: "cataloguing is finished" out here, and
  // "an operator marked this ready to sell" inside an auction. Assigning a
  // catalogued item to an auction therefore flipped its badge to Draft with no
  // operator action and no write to the lot. `ready` is now reachable ONLY
  // inside an auction, where it means the `readied_at` stamp — one word, one
  // meaning. Retires transition-matrix §3.2 and amends ADR 028 — Lot Inversion.
  //
  // The early return also matters for the SQL twin: `readied_at` IS set on a
  // few unassociated lots in practice (1 of 20 on dev), so `lotPhaseWhere`
  // carries an explicit `auction_id IS NOT NULL` guard rather than relying on
  // rung ordering it does not have.
  if (auction == null) {
    return { kind: 'draft' };
  }

  // Runtime open / awaiting / prep. The bidding window has opened iff the
  // auction has opened and the lot's bidding-open stamp is set, OR the auction
  // is accepting pre-bids and the lot's pre-bidding stamp is set.
  //
  // BL-1291: pre-bidding used to ask the LOT alone, while bidding asked auction
  // then lot. That asymmetry is what let one leftover lot pin an auction in
  // pre-bidding and made "close pre-bidding" a per-lot chase. Both windows are
  // now two-stamp, so clearing `auctions.pre_bidding_at` darkens every lot at
  // once — writing no lot row, which is what makes the recovery lossless.
  const windows = deriveLotWindows(lot, auction);
  const windowOpen = windows.biddingOpen || windows.preBiddingOpen;
  const closeMs = lot.closeTime == null ? null : Date.parse(lot.closeTime);
  // BUG-835: a timed lot is only biddable if it has a SCHEDULED close (a future
  // close_time). A null close_time means it was never ring-assigned / staggered,
  // so it is NOT open — it stays "Coming Soon" (falls through to a prep state).
  // Treating a null close_time as "still open" let the bid gate accept bids on an
  // unscheduled lot. Guards both the `open-for-bidding` and `pre-bidding` branches.
  const beforeClose = closeMs != null && now.getTime() < closeMs;

  if (windowOpen && closeMs != null && now.getTime() >= closeMs) {
    // BUG-909: a manual-close lot keeps accepting bids past its scheduled
    // close (a clerk resolves it) — surfaced as the distinct `last-call`
    // kind so every consumer (bid gate, grid card, detail panel) agrees by
    // construction instead of each re-deriving `closingType === 'manual'`.
    if (auction.closingType === 'manual') {
      return { kind: 'last-call', closedAt: lot.closeTime as string };
    }
    return { kind: 'awaiting-resolution', closedAt: lot.closeTime as string };
  }
  if (windows.biddingOpen && beforeClose) {
    return { kind: 'open-for-bidding', closesAt: lot.closeTime };
  }
  if (windows.preBiddingOpen && beforeClose) {
    return { kind: 'pre-bidding', closesAt: lot.closeTime };
  }

  // Prep states (operator intent, window not open). A lot with biddingOpenedAt
  // set but no open window (auction not opened, or no scheduled close_time —
  // the BUG-835 guard) falls through to `ready`: bidding_opened_at is only ever
  // stamped alongside readied_at (LOT_STATUS_STAMP_INTENT `active`,
  // stampAuctionOpenDecisionStamps), and both `ready` and the retired
  // `published` rung rendered identically as Coming Soon (BL-772).
  if (lot.readiedAt != null) return { kind: 'ready' };
  return { kind: 'draft' };
}
